/* ===========================================================
   COMPARE — side-by-side product comparison table
   =========================================================== */
import React from "react";
import { ProductTile, STOCK_PILL } from "@/components/product-card";
import { Breadcrumbs, toast } from "@/components/shell";
import { inr } from "@/data/data";
import { Icons } from "@/lib/icons";
import { useProductsBySlugs } from "@/api/hooks";

const { useState: cmpUS, useEffect: cmpUE } = React;

const COMPARE_KEY = "nethrasap.compare";
const MAX_COMPARE = 4;

function readSlugs() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(COMPARE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string").slice(0, MAX_COMPARE) : [];
  } catch (_) {
    return [];
  }
}

function writeSlugs(slugs) {
  try { window.localStorage.setItem(COMPARE_KEY, JSON.stringify(slugs)); } catch (_) {}
}

function ComparePage() {
  const [slugs, setSlugs] = cmpUS(readSlugs);
  cmpUE(() => { writeSlugs(slugs); }, [slugs]);

  // Fetch each slug in parallel; reuse the product cache from the PDP route.
  const queries = useProductsBySlugs(slugs);
  const items = queries.map((q) => q.data).filter(Boolean);
  const isLoading = queries.some((q) => q.isLoading);

  const remove = (slug) => setSlugs((arr) => arr.filter((s) => s !== slug));

  // rows definition
  const rows = [
    { k: "category",     label: "Category",          getValue: (p) => p.category_name },
    { k: "brand",        label: "Brand",             getValue: (p) => p.brand },
    { k: "price",        label: "Price",             getValue: (p) => inr(p.price_min), bestLow: true, sortBy: (p) => p.price_min },
    { k: "unit",         label: "Pack size",         getValue: (p) => p.unit_label },
    { k: "stock",        label: "Stock",             getValue: (p) => STOCK_PILL[p.stock_status].label },
    { k: "rating",       label: "Rating",            getValue: (p) => (p.rating ?? 0).toFixed(1) + " ★", bestHigh: true, sortBy: (p) => p.rating ?? 0 },
    { k: "reviews",      label: "Reviews",           getValue: (p) => (p.reviews ?? 0).toLocaleString("en-IN"), bestHigh: true, sortBy: (p) => p.reviews ?? 0 },
    { k: "schedule",     label: "Schedule",          getValue: (p) => p.schedule || "Non-scheduled" },
    { k: "delivery",     label: "Dispatch",          getValue: (p) => p.delivery_time_mins ? p.delivery_time_mins + " min" : "—", bestLow: true, sortBy: (p) => p.delivery_time_mins || 99999 },
    { k: "badge",        label: "Tag",               getValue: (p) => p.badge || "—" },
    { k: "hsn",          label: "HSN code",          getValue: (p) => p.hsn_code },
  ];

  const bestForRow = (row) => {
    if (!row.sortBy || items.length < 2) return null;
    const vals = items.map((p) => row.sortBy(p));
    const target = row.bestLow ? Math.min(...vals) : Math.max(...vals);
    const winners = items.filter((p) => row.sortBy(p) === target);
    if (winners.length === 1) return winners[0].id;
    return null;
  };

  return (
    <>
      <Breadcrumbs items={[{ label: "Compare" }]} />
      <div className="container compare-page">
        <header style={{ padding: "32px 0 24px", display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: "clamp(28px, 3.6vw, 40px)", letterSpacing: "-0.025em" }}>Compare products</h1>
            <p style={{ color: "var(--ink-3)", fontSize: 15, marginTop: 8 }}>Side-by-side comparison · {items.length} of 4 slots filled</p>
          </div>
          <a href="/pages/storefront/products.html" className="btn btn-outline btn-sm">Add from catalog <Icons.Plus size={14} stroke={2} /></a>
        </header>

        {isLoading && slugs.length > 0 ? (
          <div className="empty"><div className="empty-title">Loading…</div></div>
        ) : items.length < 2 ? (
          <div className="empty">
            <div className="empty-ic"><Icons.Hash size={26} stroke={1.6} /></div>
            <div className="empty-title">Add at least two products to compare</div>
            <div className="empty-desc">Browse the catalog and tap the compare icon on any product card. We'll line them up by price, dispatch time, rating and specs.</div>
            <div className="empty-actions">
              <a className="btn btn-primary" href="/pages/storefront/products.html">Browse catalog</a>
            </div>
          </div>
        ) : (
          <div className="compare-grid" style={{ "--cols": items.length }}>
            {/* Header row */}
            <div className="compare-cell compare-cell-row-k" style={{ alignItems: "flex-end" }}>
              <span>Product</span>
            </div>
            {items.map((p) => (
              <div key={p.id} className="compare-cell compare-cell-header">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <span className={"pill " + STOCK_PILL[p.stock_status].cls} style={{ alignSelf: "flex-start" }}>{STOCK_PILL[p.stock_status].label}</span>
                  <button className="compare-remove" onClick={() => remove(p.slug)} aria-label="Remove"><Icons.X size={14} /></button>
                </div>
                <ProductTile product={p} size="md" />
                <a href={"/pages/storefront/product.html?slug=" + p.slug} style={{ fontWeight: 500, fontSize: 14 }}>{p.name}</a>
                <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{p.brand} · {p.unit_label}</div>
                <button className="btn btn-primary btn-sm" onClick={() => toast && toast("Added " + p.name + " to cart")}>
                  <Icons.Plus size={13} stroke={2} /> Add to cart
                </button>
              </div>
            ))}

            {/* Spec rows */}
            {rows.map((row) => {
              const bestId = bestForRow(row);
              return (
                <React.Fragment key={row.k}>
                  <div className="compare-cell compare-cell-row-k">{row.label}</div>
                  {items.map((p) => (
                    <div key={p.id} className={"compare-cell compare-cell-v" + (bestId === p.id ? " is-best" : "")}>
                      {row.getValue(p)}
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export { ComparePage };
